import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Grid, GridItem } from "components/Grid"
import ReservationInvoiceInvoicePosition from "models/ReservationInvoiceInvoicePosition"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Spinner from "components/Spinner/Spinner"
import InputNumber from "components/InputNumber/InputNumber"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import { Guid } from "@/types"
import CustomError from "models/CustomError"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import Text from "components/Text/Text"
import { Flex } from "components/Flex"
import ReservationInvoicePositionTextModal from "pages/ReservationPage/steps/Pricing/ReservationInvoicePositionTextModal"
import NoValue from "components/NoValue/NoValue"
import { PartialNoteType } from "./PartialNoteCreateModal"
import { Currency } from "config/constants"
import useTenantCurrency from "hooks/useTenantCurrency"
import { applyExchangeRate } from "stores/exchangeRates"

interface IPartialNoteInvoicePositionProps {
    partialNoteType: PartialNoteType
    invoicePosition: ReservationInvoiceInvoicePosition
    currency: Currency | null
    exchangeRate: number | null
    onAmountChange(params: { id: Guid, inputValue: string, amount: number | null }): void
    validationError?: CustomError
}

const PartialNoteInvoicePosition: FC<IPartialNoteInvoicePositionProps> = ({
    partialNoteType,
    invoicePosition,
    currency,
    exchangeRate,
    onAmountChange,
    validationError,
}) => {
    const { t } = useTranslation()
    const tenantCurrency = useTenantCurrency()
    const [amount, amountNumber, setAmount] = useInputNumber("")
    const [showTextModal, setShowTextModal] = useState(false)
    const isCredit = partialNoteType === PartialNoteType.credit
    const isDebit = partialNoteType === PartialNoteType.debit

    useEffect(() => {
        onAmountChange({
            id: invoicePosition.id,
            inputValue: amount,
            amount: amountNumber,
        })
    }, [amount, amountNumber, invoicePosition.id, onAmountChange])

    return (
        <>
            <Grid
                columns="minmax(0, auto) repeat(3, 18.8rem)"
                alignItems="center"
                gap="3.2rem"
                className="mb-1">
                <GridItem>
                    {invoicePosition.isHydrated
                        ? <>
                            <div className="font-bold">
                                <FlexibleContent text={invoicePosition.description} clamp={1} />
                            </div>
                        </>
                        : <Flex inline>
                            <Spinner size={24} />
                        </Flex>
                    }
                </GridItem>
                <GridItem className="text-right py-1">
                    {t("common.amountWithCurrency", { count: invoicePosition.amount, currency })}
                </GridItem>
                <GridItem className="text-right py-1">
                    {t("common.amountWithCurrency", { count: invoicePosition.residualAmount, currency })}
                </GridItem>
                <GridItem className="text-right">
                    {((isDebit && !!invoicePosition.creditedAmount) || (isCredit && invoicePosition.residualAmount !== 0))
                        ? <>
                            <InputNumber
                                minDecimals={2}
                                maxDecimals={2}
                                value={amount}
                                onChange={setAmount}
                                after={currency ? <div className={`px-2${validationError ? ' color-red-500' : ''}`}>{currency}</div> : undefined}
                                isSubmitted
                                isInvalid={!!validationError}
                            />
                        </>
                        : <div className="py-1">
                            {t("common.amountWithCurrency", { count: 0, currency })}
                        </div>
                    }
                </GridItem>
            </Grid>

            <Grid columns="minmax(0, auto) max-content"
                gap="3.2rem">
                <Text type="pSmallest">
                        <Grid columns="max-content minmax(0, auto)" gap="4rem">
                            <GridItem>{invoicePosition.number}</GridItem>
                            <GridItem>
                                {!!invoicePosition.text
                                    ? <FlexibleContent
                                        text={invoicePosition.text}
                                        clampedTooltip={false}
                                        clamp={2}
                                        className="white-space-pre-line line-height-p-smallest"
                                        onClick={() => setShowTextModal(true)} />
                                    : <NoValue>{t('invoicePositions.noText')}</NoValue>
                                }
                            </GridItem>
                        </Grid>
                    </Text>
                <GridItem>
                    {!!exchangeRate && !!tenantCurrency && typeof amountNumber === "number" &&
                        <Text type="pSmallest" className="text-right color-grey-500">
                            {t('common.amountWithCurrency', { count: applyExchangeRate(amountNumber, exchangeRate), currency: tenantCurrency })}
                        </Text>
                    }
                    {!!validationError &&
                        <ValidationMessage className="mt-0">{
                            validationError.messageCode === "validation.decimal"
                                ? validationError.message
                                : t(isDebit ? 'noteCreate.debitNoteAmountExceedCreditedAmount' : 'noteCreate.creditNoteAmountExceedInvoiceAmount')
                            }
                        </ValidationMessage>
                    }
                </GridItem>
            </Grid>
            {showTextModal &&
                <ReservationInvoicePositionTextModal
                    text={invoicePosition.text}
                    onClose={() => setShowTextModal(false)}
                />
            }
        </>
    )
}

export default PartialNoteInvoicePosition
